import { disputarPenaltisDetalhado, ordemDeBatedores, type FaseCopa, type NomeFase } from './copa';
import { gerarClube, gerarJogador } from './gerador';
import { PREMIO_CAMPEAO_LIBERTADORES, PREMIO_CAMPEAO_SULAMERICANA } from './financas';
import type { EstadoJogo } from './jogo';
import { calcularTabela, type Confronto, type LinhaTabela, type ResultadoJogo } from './liga';
import { jogadorDeExterno, MINIMO_POR_POSICAO } from './mercado';
import { clubesDaLiga, jogadoresExternos } from './ligas-mundo';
import { classificadosDaLiga } from './ligas-mundo-sim';
import type { Rng } from './rng';
import { POSICOES, type Clube, type Posicao } from './tipos';

/**
 * Competições continentais: Copa Libertadores e Copa Sul-Americana, com a mesma estrutura (`Libertadores`, campo `tipo`).
 *
 * Fase de grupos: grupos de 4 clubes, ida e volta (6 rodadas). Hoje só Brasil e Argentina têm campeonato no jogo, então cada copa
 * tem `GRUPOS_CONTINENTAL` = 4 grupos (16 clubes: 8 do Brasil e 8 da Argentina). Quando houver as outras ligas da América do Sul,
 * basta subir a constante para 8 grupos (32 clubes) e distribuir as vagas por país (ver `vagasDoTorneio`).
 *
 * Libertadores: os 2 primeiros de cada grupo vão ao mata-mata (quartas com 4 grupos, oitavas com 8; quartas e semifinais em ida e
 * volta, sem gol fora; empate no agregado vai aos pênaltis; final em jogo único). Os terceiros colocados vão à Sul-Americana.
 * Sul-Americana: os líderes de grupo esperam; os segundos enfrentam, em ida e volta, os terceiros da Libertadores (Playoffs); os
 * vencedores dos playoffs pegam os líderes na primeira fase do mata-mata, que segue igual à da Libertadores.
 *
 * Os clubes brasileiros são os do jogo; os argentinos ficam em `e.libertadores.clubesExternos` (fora de `e.clubes`, então não entram
 * no mercado, nas tabelas nem na Procura) e servem às duas copas. As rodadas ocupam índices reservados do calendário misto (`slots`).
 */

export const LIBERTADORES_NOME = 'Copa Libertadores';
export const SULAMERICANA_NOME = 'Copa Sul-Americana';
export const ID_BASE_EXTERNOS = 1000;
/** Grupos de cada copa (de 4 clubes). Com mais ligas sul-americanas no jogo: 8. */
export const GRUPOS_CONTINENTAL = 4;
export const CLUBES_CONTINENTAL = GRUPOS_CONTINENTAL * 4;
/** Vagas diretas de cada país por copa (hoje metade para o Brasil, metade para a Argentina). */
export const VAGAS_POR_PAIS = CLUBES_CONTINENTAL / 2;
/** Depois de quantas rodadas da liga entra cada rodada de cada copa (6 de grupos, [2 de playoffs], ida e volta do mata-mata, final). */
export const CORTES_LIBERTADORES = [7, 9, 13, 15, 19, 21, 25, 27, 29, 31, 33];
export const CORTES_SULAMERICANA = [8, 10, 14, 16, 20, 22, 24, 26, 28, 30, 32, 34, 36];
export const RODADAS_LIBERTADORES = CORTES_LIBERTADORES.length;
export const RODADAS_SULAMERICANA = CORTES_SULAMERICANA.length;
/** Os cortes das duas copas juntos, para `iniciarCopaJunta` (que devolve as rodadas extras na mesma ordem). */
export const CORTES_CONTINENTAIS = [...CORTES_LIBERTADORES, ...CORTES_SULAMERICANA].sort((a, b) => a - b);

export type TipoContinental = 'lib' | 'sul';

const PREMIO_VITORIA_GRUPO: Record<TipoContinental, number> = { lib: 500_000, sul: 250_000 };
const PREMIO_CLASSIFICAR_GRUPOS: Record<TipoContinental, number> = { lib: 4_000_000, sul: 2_000_000 };
const PREMIO_AVANCAR: Record<TipoContinental, Record<NomeFase, number>> = {
  lib: { Playoffs: 0, 'Oitavas de final': 4_000_000, 'Quartas de final': 5_000_000, Semifinais: 6_000_000, Final: 20_000_000 },
  sul: { Playoffs: 1_000_000, 'Oitavas de final': 2_000_000, 'Quartas de final': 2_500_000, Semifinais: 3_000_000, Final: 8_000_000 },
};
export const PREMIO_VICE_LIBERTADORES = 10_000_000;
export const PREMIO_VICE_SULAMERICANA = 5_000_000;

export interface Libertadores {
  nome: string;
  /** ausente = Libertadores */
  tipo?: TipoContinental;
  /** os clubes dos grupos (ids de `e.clubes` e de `clubesExternos`) */
  participantes: number[];
  /** clubes estrangeiros; a Sul-Americana usa os da Libertadores (a lista dela fica vazia) */
  clubesExternos: Clube[];
  /** país de cada participante, pelo id */
  paises: Record<number, string>;
  grupos: number[][];
  /** os índices do calendário reservados à competição: 6 de grupos, depois as fases do mata-mata em ordem */
  slots: number[];
  fasesMata: FaseCopa[];
  gruposConcluidos: boolean;
  /** Libertadores: os terceiros colocados dos grupos, que vão aos playoffs da Sul-Americana */
  terceiros?: number[];
  /** Sul-Americana: os líderes de grupo, que esperam os vencedores dos playoffs */
  lideres?: number[];
  campeaoId: number | null;
  viceId: number | null;
}

/** Fases do mata-mata, em ordem. */
export function fasesDoTorneio(tipo: TipoContinental, grupos = GRUPOS_CONTINENTAL): NomeFase[] {
  const base: NomeFase[] = grupos >= 8 ? ['Oitavas de final', 'Quartas de final', 'Semifinais', 'Final'] : ['Quartas de final', 'Semifinais', 'Final'];
  return tipo === 'sul' ? ['Playoffs', ...base] : base;
}

const rodadasDaFase = (nome: NomeFase): number => (nome === 'Final' ? 1 : 2);

export function tipoDe(lib: Libertadores): TipoContinental {
  return lib.tipo ?? 'lib';
}

/** As competições continentais do save (Libertadores e, depois, Sul-Americana). */
export function continentais(e: EstadoJogo): Libertadores[] {
  return [e.libertadores, e.sulamericana].filter((x): x is Libertadores => Boolean(x));
}

export function continentalDaRodada(e: EstadoJogo, rodada: number): Libertadores | undefined {
  return continentais(e).find((c) => c.slots.includes(rodada));
}

/** Sigla (e, por ela, estádio e capacidade) dos clubes argentinos mais fortes da planilha. */
const SIGLA_ARGENTINA: Record<string, string> = {
  'CA River Plate': 'RIV',
  'CA Boca Juniors': 'BOC',
  'CA Rosario Central': 'ROS',
  'Racing Club': 'RAC',
  'CA Lanús': 'LAN',
  'CA Vélez Sarsfield': 'VEL',
};

const COR_ARGENTINA: Record<string, [string, string]> = {
  'CA River Plate': ['#ffffff', '#c8102e'],
  'CA Boca Juniors': ['#0b3d91', '#f2c200'],
  'CA Rosario Central': ['#0b3d91', '#f2c200'],
  'Racing Club': ['#6ec1e4', '#ffffff'],
  'CA Lanús': ['#8b1a3d', '#ffffff'],
  'CA Vélez Sarsfield': ['#ffffff', '#0b3d91'],
};
const CORES_PADRAO: [string, string][] = [['#c8102e', '#ffffff'], ['#0b3d91', '#ffffff'], ['#111111', '#ffffff'], ['#0a7d3a', '#ffffff'], ['#f2c200', '#0b3d91'], ['#6ec1e4', '#ffffff']];

/** Procura o clube também entre os estrangeiros das copas. */
export function clubeExterno(e: EstadoJogo, id: number): Clube | undefined {
  return e.libertadores?.clubesExternos.find((c) => c.id === id);
}

/** Todos os clubes cujos jogadores jogam: os do campeonato e os estrangeiros das copas. */
export function clubesComExternos(e: EstadoJogo): Clube[] {
  return e.libertadores ? [...e.clubes, ...e.libertadores.clubesExternos] : e.clubes;
}

export function completarElenco(c: Clube, proximoId: { valor: number }, rng: Rng): void {
  for (const pos of POSICOES as readonly Posicao[]) {
    while (c.elenco.filter((j) => j.posicao === pos).length < MINIMO_POR_POSICAO[pos]) c.elenco.push(gerarJogador(proximoId.valor++, pos, false, c.nivel, rng));
  }
}

type Externos = { clubes: Clube[]; paises: Record<number, string> };

/** Cria um clube estrangeiro com elenco gerado (nível dado). */
function novoClubeExterno(externos: Externos, nome: string, sigla: string, pais: string, nivel: number, cores: [string, string], proximoId: { valor: number }, rng: Rng): Clube {
  const id = Math.max(ID_BASE_EXTERNOS - 1, ...externos.clubes.map((c) => c.id)) + 1;
  const c = gerarClube(id, nome, sigla, cores, nivel, proximoId, rng);
  externos.paises[id] = pais;
  externos.clubes.push(c);
  return c;
}

/** Cria um clube argentino da planilha (elenco e força reais) quando ele ganha a primeira vaga continental. */
function criarClubeArgentino(externos: Externos, nome: string, proximoId: { valor: number }, rng: Rng): Clube {
  const info = clubesDaLiga('ar-1').find((c) => c.nome === nome);
  const nivel = Math.max(9, Math.min(16, Math.round((info?.forca ?? 35) / 3.3)));
  const proximoNumero = externos.clubes.length;
  const c = novoClubeExterno(externos, nome, SIGLA_ARGENTINA[nome] ?? `AR${proximoNumero}`, 'Argentina', nivel, COR_ARGENTINA[nome] ?? CORES_PADRAO[proximoNumero % CORES_PADRAO.length]!, proximoId, rng);
  const jogadores = jogadoresExternos()
    .filter((x) => x.ligaId === 'ar-1' && x.clube === nome)
    .map((x) => jogadorDeExterno(x, proximoId.valor++, nivel, rng))
    .sort((p, q) => q.overall - p.overall);
  const goleiros = jogadores.filter((j) => j.posicao === 'G').slice(0, 3);
  const outros = jogadores.filter((j) => j.posicao !== 'G').slice(0, 27);
  c.elenco = [...goleiros, ...outros];
  completarElenco(c, proximoId, rng);
  for (const j of c.elenco) j.contratoDias = 365;
  return c;
}

/** Os clubes estrangeiros de que as copas partem (nenhum: os argentinos entram conforme a liga deles). */
export function criarClubesExternos(): Externos {
  return { clubes: [], paises: {} };
}

/** Sorteio dos grupos: potes por nível (um clube de cada pote por grupo), sem dois clubes do mesmo país no grupo quando dá. */
export function sortearGrupos(ids: number[], nivelDe: (id: number) => number, paisDe: (id: number) => string, rng: Rng): number[][] {
  const n = Math.floor(ids.length / 4);
  const ordenados = rng.embaralhar([...ids]).sort((a, b) => nivelDe(b) - nivelDe(a));
  const potes = [0, 1, 2, 3].map((p) => ordenados.slice(p * n, p * n + n));
  let melhor: number[][] = [];
  let menorConflito = Infinity;
  for (let tentativa = 0; tentativa < 400; tentativa++) {
    const grupos: number[][] = Array.from({ length: n }, () => []);
    for (const pote of potes) {
      const sorteio = rng.embaralhar([...pote]);
      sorteio.forEach((id, g) => (grupos[g] as number[]).push(id));
    }
    let conflitos = 0;
    for (const g of grupos) {
      const paises = g.map(paisDe);
      conflitos += paises.length - new Set(paises).size;
    }
    if (conflitos < menorConflito) {
      menorConflito = conflitos;
      melhor = grupos;
      if (conflitos === 0) break;
    }
  }
  return melhor;
}

/** As 6 rodadas de um grupo [a, b, c, d]: três de ida e as mesmas três com o mando invertido. */
function rodadasDoGrupo(g: number[]): Confronto[][] {
  const [a, b, c, d] = g as [number, number, number, number];
  const ida: Confronto[][] = [
    [{ mandante: a, visitante: b }, { mandante: c, visitante: d }],
    [{ mandante: d, visitante: a }, { mandante: b, visitante: c }],
    [{ mandante: a, visitante: c }, { mandante: d, visitante: b }],
  ];
  const volta = ida.map((r) => r.map((x) => ({ mandante: x.visitante, visitante: x.mandante })));
  return [...ida, ...volta];
}

/** Os brasileiros do primeiro ano (sem temporada anterior), do mais forte da Série A para baixo. */
export function melhoresBrasileiros(e: EstadoJogo): number[] {
  const elite = e.acesso ? e.clubes.filter((c) => c.divisao === 1) : e.clubes;
  return [...elite].sort((a, b) => b.nivel - a.nivel || a.id - b.id).slice(0, VAGAS_POR_PAIS * 2).map((c) => c.id);
}

/**
 * Brasileiros classificados para a temporada seguinte, em ordem de prioridade: campeão da Libertadores e da Sul-Americana (se
 * brasileiros), campeão da Copa do Brasil, os 4 primeiros da Série A e depois o resto da tabela. A Libertadores fica com os
 * `VAGAS_POR_PAIS` primeiros; a Sul-Americana, com os seguintes (ver `dividirVagas`).
 */
export function brasileirosQualificados(e: EstadoJogo, tabelaA: LinhaTabela[]): number[] {
  const lista: number[] = [];
  const add = (id: number | null | undefined): void => {
    if (id != null && id < ID_BASE_EXTERNOS && !lista.includes(id) && e.clubes.some((c) => c.id === id)) lista.push(id);
  };
  add(e.libertadores?.campeaoId);
  add(e.copa?.campeaoId);
  for (const l of tabelaA.slice(0, 4)) add(l.clubeId);
  add(e.sulamericana?.campeaoId);
  for (const l of tabelaA) add(l.clubeId);
  return lista.slice(0, VAGAS_POR_PAIS * 2);
}

export interface VagasContinentais {
  lib: number[];
  sul: number[];
}

/** Divide a lista de brasileiros em ordem de prioridade entre a Libertadores (primeiros) e a Sul-Americana (seguintes). */
export function dividirVagas(ordem: number[]): VagasContinentais {
  return { lib: ordem.slice(0, VAGAS_POR_PAIS), sul: ordem.slice(VAGAS_POR_PAIS, VAGAS_POR_PAIS * 2) };
}

/** A que copa pertence cada rodada extra devolvida por `iniciarCopaJunta` (elas vêm na ordem dos cortes). */
export function separarSlots(slots: number[]): { lib: number[]; sul: number[] } {
  const ordem = [...CORTES_LIBERTADORES.map((c) => ({ c, t: 'lib' as const })), ...CORTES_SULAMERICANA.map((c) => ({ c, t: 'sul' as const }))].sort((a, b) => a.c - b.c);
  const r = { lib: [] as number[], sul: [] as number[] };
  slots.forEach((s, i) => (ordem[i]?.t === 'sul' ? r.sul : r.lib).push(s));
  return r;
}

function montar(tipo: TipoContinental, ids: number[], paises: Record<number, string>, nivelDe: (id: number) => number, rng: Rng, slots: number[], e: EstadoJogo): Libertadores {
  const grupos = sortearGrupos(ids, nivelDe, (id) => paises[id] ?? '?', rng);
  for (let r = 0; r < 6; r++) e.calendario[slots[r] as number] = grupos.flatMap((g) => rodadasDoGrupo(g)[r] as Confronto[]);
  return {
    nome: tipo === 'lib' ? LIBERTADORES_NOME : SULAMERICANA_NOME,
    ...(tipo === 'sul' ? { tipo } : {}),
    participantes: ids,
    clubesExternos: [],
    paises: { ...paises },
    grupos,
    slots,
    fasesMata: [],
    gruposConcluidos: false,
    campeaoId: null,
    viceId: null,
  };
}

/**
 * Prepara a edição da temporada das duas copas: cria (ou reaproveita) os clubes argentinos, sorteia os grupos e preenche as 6
 * rodadas de grupos nos índices reservados do calendário. `slots` são os índices vazios deixados por `iniciarCopaJunta`.
 */
export function iniciarContinentais(e: EstadoJogo, rng: Rng, brasileiros: VagasContinentais, slots: number[]): void {
  const proximoId = { valor: e.proximoIdJogador };
  const anterior = e.libertadores;
  const externos: Externos = anterior ? { clubes: [...anterior.clubesExternos], paises: { ...anterior.paises } } : criarClubesExternos();
  // Argentina: as vagas que o Brasil não preenche e o resto, na ordem da liga (os clubes novos entram com o elenco da planilha)
  const faltaLib = CLUBES_CONTINENTAL - brasileiros.lib.length;
  const faltaSul = CLUBES_CONTINENTAL - brasileiros.sul.length;
  const nomes = classificadosDaLiga(e, 'ar-1', faltaLib + faltaSul);
  // jogo com o mundo inteiro: os argentinos são os clubes de verdade da liga (com a sua tabela, caixa e elenco vivos); senão, cópias da planilha
  const argentinos = nomes
    .map((nome) => (e.mundo ? e.clubes.find((c) => c.liga === 'ar-1' && c.nome === nome) : undefined) ?? externos.clubes.find((c) => c.nome === nome && externos.paises[c.id] === 'Argentina') ?? (e.mundo ? undefined : criarClubeArgentino(externos, nome, proximoId, rng)))
    .filter((c): c is Clube => Boolean(c));
  e.proximoIdJogador = proximoId.valor;
  const paises: Record<number, string> = { ...externos.paises };
  for (const c of argentinos) paises[c.id] = 'Argentina';
  for (const id of [...brasileiros.lib, ...brasileiros.sul]) paises[id] = 'Brasil';
  const todos = [...e.clubes, ...externos.clubes];
  const nivelDe = (id: number): number => (todos.find((c) => c.id === id) as Clube).nivel;
  const idsLib = [...brasileiros.lib, ...argentinos.slice(0, faltaLib).map((c) => c.id)];
  const idsSul = [...brasileiros.sul, ...argentinos.slice(faltaLib, faltaLib + faltaSul).map((c) => c.id)];
  const partes = separarSlots(slots);
  const lib = montar('lib', idsLib, paises, nivelDe, rng, partes.lib, e);
  lib.clubesExternos = externos.clubes;
  e.libertadores = lib;
  if (partes.sul.length >= 6) e.sulamericana = montar('sul', idsSul, paises, nivelDe, rng, partes.sul, e);
  else delete e.sulamericana;
}

/** Qualquer rodada de uma das copas continentais (usada para separar resultados e cartões das outras competições). */
export function ehRodadaDeLibertadores(e: EstadoJogo, rodada: number): boolean {
  return Boolean(e.libertadores?.slots.includes(rodada) || e.sulamericana?.slots.includes(rodada));
}

function resultadosDe(e: EstadoJogo, rodadas: number[]): ResultadoJogo[] {
  return e.resultados.filter((r) => rodadas.includes(r.rodada));
}

/** Classificação de um grupo: pontos, vitórias, saldo, gols (e o id como último critério). */
export function classificacaoDoGrupo(e: EstadoJogo, grupo: number[], competicao: Libertadores | undefined = e.libertadores): LinhaTabela[] {
  const lib = competicao as Libertadores;
  const t = calcularTabela(grupo, resultadosDe(e, lib.slots.slice(0, 6)));
  return t.sort((a, b) => b.pts - a.pts || b.v - a.v || b.sg - a.sg || b.gp - a.gp || a.clubeId - b.clubeId);
}

function adicionarFaseCont(e: EstadoJogo, lib: Libertadores, nome: NomeFase, pares: [number, number][]): void {
  const confrontos = pares.map(([a, b]) => ({ a, b }));
  const usados = lib.fasesMata.reduce((s, f) => s + f.rodadas.length, 0);
  const quantas = rodadasDaFase(nome);
  const rodadas: number[] = [];
  for (let i = 0; i < quantas; i++) {
    const slot = lib.slots[6 + usados + i] as number;
    e.calendario[slot] = confrontos.map((c) => (i === 0 ? { mandante: c.a, visitante: c.b } : { mandante: c.b, visitante: c.a }));
    rodadas.push(slot);
  }
  lib.fasesMata.push({ nome, confrontos, rodadas, concluida: false });
}

function clubeDe(e: EstadoJogo, id: number): Clube {
  return (e.clubes.find((c) => c.id === id) ?? clubeExterno(e, id)) as Clube;
}

function pagar(e: EstadoJogo, id: number, valor: number): void {
  const c = e.clubes.find((x) => x.id === id);
  if (!c) return; // clube estrangeiro não tem caixa no jogo
  c.caixa += valor;
  c.balanco.premios += valor;
}

/** Sorteia pares entre dois lotes (a lista de `a` contra a de `b`), evitando mesmo grupo e, quando dá, mesmo país. */
function sortearPares(a: [number, number][], b: [number, number][], paisDe: (id: number) => string, rng: Rng, evitarMesmoGrupo: boolean): [number, number][] {
  let pior: [number, number][][] | null = null;
  for (let tentativa = 0; tentativa < 500; tentativa++) {
    const emb = rng.embaralhar([...b]);
    const pares: [number, number][] = [];
    let ok = true;
    let mesmoPais = 0;
    a.forEach(([idA, gA], i) => {
      const [idB, gB] = emb[i] as [number, number];
      if (evitarMesmoGrupo && gA === gB) ok = false;
      if (paisDe(idA) === paisDe(idB)) mesmoPais++;
      pares.push([idB, idA]);
    });
    if (!ok) continue;
    if (mesmoPais === 0) return rng.embaralhar(pares);
    pior ??= [pares];
  }
  return rng.embaralhar(pior?.[0] ?? a.map(([idA], i) => [(b[i] as [number, number])[0], idA] as [number, number]));
}

function golsDoJogo(e: EstadoJogo, rodada: number, mandante: number, visitante: number): [number, number] | undefined {
  return e.resultados.find((r) => r.rodada === rodada && r.mandante === mandante && r.visitante === visitante)?.gols;
}

/** Chame depois de avançar `e.rodada`: faz o andamento da Libertadores e, em seguida, o da Sul-Americana (que depende dela). */
export function avancarLibertadores(e: EstadoJogo, rng: Rng): void {
  for (const c of continentais(e)) avancarContinental(e, c, rng);
}

/** Fecha os grupos ou a fase do mata-mata que terminou, paga os prêmios e monta a próxima fase. */
function avancarContinental(e: EstadoJogo, lib: Libertadores, rng: Rng): void {
  const tipo = tipoDe(lib);
  const paisDe = (id: number): string => lib.paises[id] ?? '?';
  const sequencia = fasesDoTorneio(tipo, lib.grupos.length);
  const entrada = sequencia[tipo === 'sul' ? 1 : 0] as NomeFase;

  if (!lib.gruposConcluidos) {
    if (e.rodada <= (lib.slots[5] as number)) return;
    const primeiros: [number, number][] = [];
    const segundos: [number, number][] = [];
    const terceiros: number[] = [];
    lib.grupos.forEach((g, i) => {
      const t = classificacaoDoGrupo(e, g, lib);
      primeiros.push([(t[0] as LinhaTabela).clubeId, i]);
      segundos.push([(t[1] as LinhaTabela).clubeId, i]);
      terceiros.push((t[2] as LinhaTabela).clubeId);
      for (const l of t) pagar(e, l.clubeId, l.v * PREMIO_VITORIA_GRUPO[tipo]);
      pagar(e, (t[0] as LinhaTabela).clubeId, PREMIO_CLASSIFICAR_GRUPOS[tipo]);
      pagar(e, (t[1] as LinhaTabela).clubeId, PREMIO_CLASSIFICAR_GRUPOS[tipo]);
    });
    lib.gruposConcluidos = true;
    if (tipo === 'lib') {
      lib.terceiros = terceiros;
      adicionarFaseCont(e, lib, entrada, sortearPares(primeiros, segundos, paisDe, rng, true));
      return;
    }
    // Sul-Americana: os segundos enfrentam os terceiros da Libertadores (que fazem o primeiro jogo em casa); se não houver, os terceiros dela
    lib.lideres = primeiros.map(([id]) => id);
    const vindos = e.libertadores?.terceiros?.length === segundos.length ? e.libertadores.terceiros : terceiros;
    lib.terceiros = terceiros;
    adicionarFaseCont(e, lib, 'Playoffs', sortearPares(segundos, vindos.map((id, i) => [id, i + 100] as [number, number]), paisDe, rng, false));
    return;
  }

  const fase = lib.fasesMata.find((f) => !f.concluida);
  if (!fase) return;
  const ultima = fase.rodadas[fase.rodadas.length - 1] as number;
  if (e.rodada <= ultima) return;

  const vencedores: number[] = [];
  for (const c of fase.confrontos) {
    c.idaGols = golsDoJogo(e, fase.rodadas[0] as number, c.a, c.b);
    if (fase.rodadas.length > 1) c.voltaGols = golsDoJogo(e, fase.rodadas[1] as number, c.b, c.a);
    const gA = (c.idaGols?.[0] ?? 0) + (c.voltaGols?.[1] ?? 0);
    const gB = (c.idaGols?.[1] ?? 0) + (c.voltaGols?.[0] ?? 0);
    if (gA !== gB) c.vencedor = gA > gB ? c.a : c.b;
    else {
      // sem gol fora e sem prorrogação nas ida e volta: empate no agregado vai direto aos pênaltis
      const decisivo = e.ultimaRodada?.partidas.find((p) => (p.mandante === c.a && p.visitante === c.b) || (p.mandante === c.b && p.visitante === c.a));
      const campoDe = (id: number): number[] => (decisivo ? decisivo.resultado.emCampoFinal[decisivo.mandante === id ? 0 : 1] : []);
      const a = clubeDe(e, c.a);
      const b = clubeDe(e, c.b);
      const disputa = disputarPenaltisDetalhado(a, b, rng, ordemDeBatedores(a, campoDe(c.a)), ordemDeBatedores(b, campoDe(c.b)));
      c.penaltis = disputa.gols;
      c.cobrancas = disputa.cobrancas;
      c.vencedor = c.penaltis[0] > c.penaltis[1] ? c.a : c.b;
    }
    vencedores.push(c.vencedor);
    pagar(e, c.vencedor, PREMIO_AVANCAR[tipo][fase.nome]);
    if (fase.nome === 'Final') {
      const perdedor = c.vencedor === c.a ? c.b : c.a;
      pagar(e, perdedor, tipo === 'lib' ? PREMIO_VICE_LIBERTADORES : PREMIO_VICE_SULAMERICANA);
      lib.campeaoId = c.vencedor;
      lib.viceId = perdedor;
      pagar(e, c.vencedor, tipo === 'lib' ? PREMIO_CAMPEAO_LIBERTADORES : PREMIO_CAMPEAO_SULAMERICANA);
    }
  }
  fase.concluida = true;
  if (fase.nome === 'Final') return;
  const proxima = sequencia[sequencia.indexOf(fase.nome) + 1] as NomeFase;
  if (fase.nome === 'Playoffs') {
    // os vencedores dos playoffs pegam os líderes de grupo, que decidem a volta em casa
    const lideres: [number, number][] = (lib.lideres ?? []).map((id, i) => [id, i]);
    const venc: [number, number][] = vencedores.map((id, i) => [id, i + 100]);
    adicionarFaseCont(e, lib, proxima, sortearPares(lideres, venc, paisDe, rng, false));
    return;
  }
  const ordem = rng.embaralhar([...vencedores]);
  const pares: [number, number][] = [];
  for (let i = 0; i + 1 < ordem.length; i += 2) pares.push(rng.chance(0.5) ? [ordem[i] as number, ordem[i + 1] as number] : [ordem[i + 1] as number, ordem[i] as number]);
  adicionarFaseCont(e, lib, proxima, pares);
}

/** Até onde o clube foi em uma das copas, em texto. */
export function campanhaNaCompeticao(e: EstadoJogo, lib: Libertadores | undefined, clubeId: number): string {
  if (!lib) return 'Não participou';
  const nosGrupos = lib.grupos.some((g) => g.includes(clubeId));
  const nasFases = lib.fasesMata.some((f) => f.confrontos.some((c) => c.a === clubeId || c.b === clubeId));
  if (!nosGrupos && !nasFases) return 'Não participou';
  if (lib.campeaoId === clubeId) return 'Campeão';
  if (lib.viceId === clubeId) return 'Vice-campeão';
  let ultima: FaseCopa | undefined;
  for (const f of lib.fasesMata) if (f.confrontos.some((c) => c.a === clubeId || c.b === clubeId)) ultima = f;
  if (!ultima) {
    if (!lib.gruposConcluidos) return 'Disputando a fase de grupos';
    // terceiro da Libertadores: vai à Sul-Americana
    return 'Eliminado na fase de grupos';
  }
  if (!ultima.concluida) return `Disputando ${ultima.nome === 'Playoffs' ? 'os playoffs' : ultima.nome.toLowerCase()}`;
  if (ultima.nome === 'Final') return 'Final';
  const eliminado = { Playoffs: 'nos playoffs', 'Oitavas de final': 'nas oitavas', 'Quartas de final': 'nas quartas', Semifinais: 'nas semifinais' } as const;
  // quem perde o último confronto concluído caiu ali; quem avançou aparece na fase seguinte e não chega aqui
  return `Eliminado ${eliminado[ultima.nome as keyof typeof eliminado]}`;
}

/** Até onde o clube foi na Libertadores, em texto. */
export function campanhaNaLibertadores(e: EstadoJogo, clubeId: number): string {
  return campanhaNaCompeticao(e, e.libertadores, clubeId);
}

export function campanhaNaSulamericana(e: EstadoJogo, clubeId: number): string {
  return campanhaNaCompeticao(e, e.sulamericana, clubeId);
}

/** Texto da rodada: "Libertadores - Fase de grupos (3ª rodada)", "Sul-Americana - Playoffs (ida)". */
export function textoRodadaLibertadores(e: EstadoJogo, rodada: number): string {
  const lib = continentalDaRodada(e, rodada);
  if (!lib) return LIBERTADORES_NOME;
  const nome = tipoDe(lib) === 'lib' ? 'Libertadores' : 'Sul-Americana';
  const pos = lib.slots.indexOf(rodada);
  if (pos < 6) return `${nome} - Fase de grupos (${pos + 1}ª rodada)`;
  // a fase pela ordem das rodadas reservadas (vale também para fases que ainda não foram sorteadas)
  let acumulado = 6;
  for (const f of fasesDoTorneio(tipoDe(lib), lib.grupos.length)) {
    const n = rodadasDaFase(f);
    if (pos < acumulado + n) return `${nome} - ${f}${n === 1 ? '' : ` (${pos === acumulado ? 'ida' : 'volta'})`}`;
    acumulado += n;
  }
  return `${nome} - Mata-mata`;
}

/** Nome da competição continental de uma rodada. */
export function nomeContinentalDaRodada(e: EstadoJogo, rodada: number): string {
  const lib = continentalDaRodada(e, rodada);
  return lib ? lib.nome : LIBERTADORES_NOME;
}

/** Todas as fases de mata-mata (Copa do Brasil e copas continentais): as telas de pênaltis e do jogo de ida procuram aqui. */
export function fasesDeMataMata(e: EstadoJogo): FaseCopa[] {
  return [...(e.copa?.fases ?? []), ...(e.libertadores?.fasesMata ?? []), ...(e.sulamericana?.fasesMata ?? [])];
}
